import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../lib/rbac";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
    DialogDescription,
} from "../components/ui/dialog";
import { CalendarClock, RotateCcw, PlayCircle, ShieldCheck } from "lucide-react";

const POLICY_LABEL = {
    carry_over_capped: "Carry over — capped",
    reset_to_zero: "Reset to zero",
    payout_on_reset: "Payout on reset",
};

const POLICY_HELP = {
    carry_over_capped:
        "Unused Annual Leave rolls forward on the fiscal reset date, capped at N days. Anything above the cap is dropped and logged.",
    reset_to_zero:
        "Unused Annual Leave is wiped on the fiscal reset date. No cap; no cash payout.",
    payout_on_reset:
        "Unused Annual Leave is paid out on the fiscal reset date via a one-off payroll allowance line (days × daily rate).",
};

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];


export default function LeaveAccrualConfig() {
    const { user } = useAuth();
    const isAdmin = user?.role === ROLES.KAAFI_ADMIN;

    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [previewLoading, setPreviewLoading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [applying, setApplying] = useState(false);
    const [confirmApply, setConfirmApply] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/leave-accrual-config");
                setConfig(data);
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (!isAdmin) {
        return (
            <AppShell title="Leave Accrual Config">
                <div className="asl-card p-6" data-testid="leave-accrual-forbidden">
                    <p className="text-sm">This page is restricted to System Admins.</p>
                </div>
            </AppShell>
        );
    }

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                fiscal_reset_month: Number(config.fiscal_reset_month),
                fiscal_reset_day: Number(config.fiscal_reset_day),
                carry_over_policy: config.carry_over_policy,
                carry_over_cap_days: Number(config.carry_over_cap_days),
            };
            const { data } = await api.patch("/admin/leave-accrual-config", payload);
            setConfig(data);
            toast.success("Leave accrual config saved.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const runDryRun = async () => {
        setPreviewLoading(true);
        try {
            const { data } = await api.post(
                "/admin/leave-accrual-config/run-reset",
                { dry_run: true },
            );
            setPreview(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setPreviewLoading(false);
        }
    };

    const applyReset = async () => {
        setApplying(true);
        try {
            const { data } = await api.post(
                "/admin/leave-accrual-config/run-reset",
                { dry_run: false },
            );
            toast.success(
                `Fiscal reset applied — ${data.processed} employees, ${data.carried_total_days} carried, ${data.dropped_total_days} dropped, $${data.payout_total_amount.toFixed(2)} paid out.`,
            );
            setPreview(null);
            setConfirmApply(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setApplying(false);
        }
    };

    if (loading || !config) {
        return (
            <AppShell title="Leave Accrual Config">
                <div className="asl-card p-6" data-testid="leave-accrual-loading">Loading…</div>
            </AppShell>
        );
    }

    const isCapped = config.carry_over_policy === "carry_over_capped";
    const daysInMonth = new Date(2028, Number(config.fiscal_reset_month), 0).getDate();

    return (
        <AppShell title="Leave Accrual Config">
            <section className="space-y-5" data-testid="leave-accrual-page">
                <header className="flex items-center gap-3">
                    <CalendarClock className="h-6 w-6 asl-navy-text" />
                    <div>
                        <h2 className="text-xl font-bold asl-navy-text">
                            Annual Leave Accrual Config
                        </h2>
                        <p className="text-sm text-[color:var(--asl-muted)]">
                            Controls how the 2.5-days-per-month Annual Leave accrual
                            resets each fiscal year. Somali Labor Law Article 69.
                        </p>
                    </div>
                </header>

                <div className="asl-card p-5 space-y-5" data-testid="leave-accrual-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Fiscal reset month</Label>
                            <Select
                                value={String(config.fiscal_reset_month)}
                                onValueChange={(v) => setConfig({ ...config, fiscal_reset_month: Number(v) })}
                            >
                                <SelectTrigger data-testid="fiscal-reset-month-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {MONTHS.map((name, idx) => (
                                        <SelectItem
                                            key={idx + 1}
                                            value={String(idx + 1)}
                                            data-testid={`fiscal-reset-month-opt-${idx + 1}`}
                                        >
                                            {name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Fiscal reset day</Label>
                            <Input
                                type="number"
                                min={1}
                                max={31}
                                value={config.fiscal_reset_day}
                                onChange={(e) => setConfig({ ...config, fiscal_reset_day: Number(e.target.value) })}
                                data-testid="fiscal-reset-day-input"
                            />
                            <p className="text-xs text-[color:var(--asl-muted)]">
                                {MONTHS[config.fiscal_reset_month - 1]} has {daysInMonth} days.
                                Feb-29 clamps to Feb-28 on non-leap years.
                            </p>
                        </div>
                        <div className="space-y-1 md:col-span-2">
                            <Label className="text-xs uppercase">Carry-over policy</Label>
                            <Select
                                value={config.carry_over_policy}
                                onValueChange={(v) => setConfig({ ...config, carry_over_policy: v })}
                            >
                                <SelectTrigger data-testid="carry-over-policy-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(POLICY_LABEL).map(([k, label]) => (
                                        <SelectItem
                                            key={k}
                                            value={k}
                                            data-testid={`carry-over-policy-opt-${k}`}
                                        >
                                            {label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-[color:var(--asl-muted)]">
                                {POLICY_HELP[config.carry_over_policy]}
                            </p>
                        </div>
                        {isCapped && (
                            <div className="space-y-1">
                                <Label className="text-xs uppercase">Cap (days)</Label>
                                <Input
                                    type="number"
                                    min={0}
                                    max={365}
                                    value={config.carry_over_cap_days}
                                    onChange={(e) => setConfig({ ...config, carry_over_cap_days: Number(e.target.value) })}
                                    data-testid="carry-over-cap-days-input"
                                />
                                <p className="text-xs text-[color:var(--asl-muted)]">
                                    Anything above this cap on reset date is dropped.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 border-t border-[color:var(--asl-border,#E5E7EB)] pt-4">
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="asl-navy-bg text-white hover:opacity-90"
                            data-testid="leave-accrual-save-btn"
                        >
                            <ShieldCheck className="h-4 w-4 mr-1" />
                            {saving ? "Saving…" : "Save config"}
                        </Button>
                        <Button
                            onClick={runDryRun}
                            disabled={previewLoading}
                            variant="outline"
                            className="border-[color:var(--asl-navy)] asl-navy-text hover:bg-[color:var(--asl-navy-50,#eef1f8)]"
                            data-testid="fiscal-reset-dry-run-btn"
                        >
                            <PlayCircle className="h-4 w-4 mr-1" />
                            {previewLoading ? "Computing…" : "Run fiscal reset now (dry-run)"}
                        </Button>
                    </div>
                </div>
            </section>

            <Dialog
                open={!!preview}
                onOpenChange={(o) => { if (!o) { setPreview(null); setConfirmApply(false); } }}
            >
                <DialogContent className="max-w-3xl" data-testid="fiscal-reset-preview-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            Fiscal reset — dry-run preview
                        </DialogTitle>
                        <DialogDescription>
                            {preview && (
                                <>
                                    Old cycle <span className="mono">{preview.old_cycle.start} → {preview.old_cycle.end}</span> →
                                    new cycle <span className="mono"> {preview.new_cycle.start} → {preview.new_cycle.end}</span>.
                                    Policy: <span className="font-semibold asl-navy-text">{POLICY_LABEL[preview.policy]}</span>
                                    {preview.policy === "carry_over_capped" && ` (cap: ${preview.cap_days}d)`}.
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    {preview && (
                        <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                    <div className="text-[color:var(--asl-muted)]">Processed</div>
                                    <div className="text-lg font-bold asl-navy-text" data-testid="fiscal-reset-processed">
                                        {preview.processed}
                                    </div>
                                </div>
                                <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                    <div className="text-[color:var(--asl-muted)]">Carried (days)</div>
                                    <div className="text-lg font-bold asl-navy-text" data-testid="fiscal-reset-carried">
                                        {preview.carried_total_days}
                                    </div>
                                </div>
                                <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                    <div className="text-[color:var(--asl-muted)]">Dropped (days)</div>
                                    <div className="text-lg font-bold asl-crimson-text" data-testid="fiscal-reset-dropped">
                                        {preview.dropped_total_days}
                                    </div>
                                </div>
                                <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                    <div className="text-[color:var(--asl-muted)]">Payout total</div>
                                    <div className="text-lg font-bold asl-crimson-text" data-testid="fiscal-reset-payout">
                                        ${preview.payout_total_amount.toFixed(2)}
                                    </div>
                                </div>
                            </div>
                            <div className="max-h-72 overflow-auto border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                                <table className="w-full text-xs" data-testid="fiscal-reset-preview-table">
                                    <thead className="asl-navy-bg text-white sticky top-0">
                                        <tr>
                                            <th className="text-left px-3 py-2">Employee</th>
                                            <th className="text-right px-3 py-2">Old remaining</th>
                                            <th className="text-right px-3 py-2">Carried</th>
                                            <th className="text-right px-3 py-2">Dropped</th>
                                            <th className="text-right px-3 py-2">Payout ($)</th>
                                            <th className="text-left px-3 py-2">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(preview.lines || []).slice(0, 200).map((l) => (
                                            <tr
                                                key={l.employee_id}
                                                className="border-t border-[color:var(--asl-border,#E5E7EB)]"
                                                data-testid={`fiscal-reset-row-${l.employee_id}`}
                                            >
                                                <td className="px-3 py-2">{l.employee_name || l.employee_id}</td>
                                                <td className="px-3 py-2 mono text-right">{l.old_remaining}</td>
                                                <td className="px-3 py-2 mono text-right asl-navy-text">{l.carried}</td>
                                                <td className="px-3 py-2 mono text-right asl-crimson-text">{l.dropped}</td>
                                                <td className="px-3 py-2 mono text-right asl-crimson-text">
                                                    {l.payout_amount.toFixed(2)}
                                                </td>
                                                <td className="px-3 py-2 mono">{l.action}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {confirmApply && (
                                <div className="rounded-md border border-[color:var(--asl-crimson)] bg-[color:var(--asl-crimson-50,#FDF2F5)] p-3">
                                    <p className="text-xs asl-crimson-text font-semibold">
                                        Apply this fiscal reset to the live database?
                                    </p>
                                    <p className="text-xs text-[color:var(--asl-muted)] mt-1">
                                        Writes carry-over state for {preview.processed} employees
                                        and creates {preview.payouts?.length ?? 0} payout entries.
                                        Idempotent — safe to re-run on the same day.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { setPreview(null); setConfirmApply(false); }}
                            disabled={applying}
                            data-testid="fiscal-reset-cancel"
                        >
                            Cancel
                        </Button>
                        {confirmApply ? (
                            <Button
                                className="asl-crimson-bg text-white"
                                onClick={applyReset}
                                disabled={applying}
                                data-testid="fiscal-reset-apply-confirm-btn"
                            >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                {applying ? "Applying…" : "Yes — apply reset"}
                            </Button>
                        ) : (
                            <Button
                                className="asl-crimson-bg text-white"
                                onClick={() => setConfirmApply(true)}
                                disabled={applying}
                                data-testid="fiscal-reset-apply-btn"
                            >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Apply reset
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
